import { useTranslator } from "@eo-locale/react";

import { string } from "yup";

import { Field, Input, VisuallyHidden } from "@/storybook-components";
import type { ChangeEvent } from "react";
import type { Question } from "@/types";
import { setAnswer } from "@/redux/features/questions/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";

interface EmailOrScreenNameQuestionProps {
  question: Partial<Question>;
}

export const validate = async (
  answer: string,
  question: Partial<Question>
): Promise<string[]> => {
  const schema = string().required();
  try {
    await schema.validate(answer);
    return [];
  } catch (err) {
    return err.errors;
  }
};

const EmailOrScreenNameQuestion = ({
  question
}: EmailOrScreenNameQuestionProps): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const { answers, errors, status } = useAppSelector(
    (state) => state.questions
  );

  const error = errors[question.id] || [];
  const answer = answers[question.id] || "";

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    dispatch(
      setAnswer({
        questionId: question.id,
        answer: e.target.value
      })
    );
  };

  return (
    <Field.Root
      my={4}
      id={`${question.id}`}
      invalid={error.length > 0}
      required={question.required}
      disabled={status === "saving"}
      className={error.length > 0 ? "ehqQuestionError" : null}
    >
      <Field.Label>
        {translate("questions.login.emailOrScreenName")}
        {question.required && (
          <VisuallyHidden>{`* ${translate("common.required")}`}</VisuallyHidden>
        )}
      </Field.Label>
      <Input
        type="text"
        value={answer}
        onChange={handleChange}
        maxLength={question.maxLength}
        data-testid="emailOrScreenNameQuestion-input"
        size={{ base: "lg", lg: "md" }}
        name="emailOrScreenNameQuestion-input"
        autoComplete="off"
      />
      {error.length > 0 && (
        <Field.ErrorText data-testid="emailOrScreenNameQuestion-errorMessage">
          <Field.ErrorIcon />
          {translate(`errors.${error}`)}
        </Field.ErrorText>
      )}
      <Field.HelperText>{question.notes}</Field.HelperText>
    </Field.Root>
  );
};

export default EmailOrScreenNameQuestion;
